import React, { useState } from 'react';
import { t } from 'i18next';
import { Grid2, Skeleton, Stack } from '@mui/material';
import IntegrationCard from '../Shared/IntegrationCard';
import POSDeleteModal from '@/components/POS/Common/POSDeleteModal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '@/utils/Api/POS';
import { CategoryAddon } from '../../Types/Integration-type';
import CardComponent from '../../Components/CardComponent';

const economic_logo: string = require('@/assets/economic.png');
const billy: string = require('@/assets/billy.png');
const dinero: string = require('@/assets/dinero.png');

type LoadingState = {
    [key: string]: boolean;
};

interface POSIntegrationPropsType {
    CategoryAddon: CategoryAddon;
    fetchData: () => void;
    settings: any;
    isLoading?: boolean;
}

const ADDON_IDS = {
    dinero: 1,
    economic: 2,
    billy: 3,
} as const;

const POSIntegration = ({ CategoryAddon, fetchData, settings, isLoading }: POSIntegrationPropsType) => {
    const navigate = useNavigate();
    const [closeTab, setCloseTab] = useState(false);
    const [loadingState, setLoadingState] = useState<LoadingState>({});
    const [deactivateLoading, setDeactivateLoading] = useState(false);
    const [integrationName, setIntegrationName] = useState('');

    const getAddon = (id: number) => CategoryAddon?.addons?.find((a) => a.id === id);

    const handleDeactive = async (name: string) => {
        setDeactivateLoading(true);
        try {
            if (name === 'economic') {
                await api.postApiEconomicDisconnect();
            }
            if (name === 'dinero') {
                await api.deleteApiAddonsAddonId(String(ADDON_IDS.dinero));
            }
            setCloseTab(false);
            fetchData();
            setDeactivateLoading(false);
        } catch (error) {
            console.error('error', error);
            toast.error(t('Integration.DeactivationFailedMessage'));
        }
    };

    const handleBillyActivate = async () => {
        try {
            setLoadingState((prev) => ({ ...prev, billy: true }));
            await api.postApiAddonsUpsert({ addonName: 'billy' });
            navigate('/settings/integration/billy-setup');
        } catch (error) {
            console.error('error ', error);
            toast.error(t('Integration.FailedToActivate'));
        } finally {
            setLoadingState((prev) => ({ ...prev, billy: false }));
        }
    };

    const handleDineroActivate = async () => {
        try {
            setLoadingState((prev) => ({ ...prev, dinero: true }));
            await api.postApiAddonsUpsert({ addonName: 'dinero' });
            const res = await api.getApiAddons();
            const posCategory = res.data?.find((c) => c.categoryId === 1);
            const dineroAddon = posCategory?.addons?.find((a) => a.id === ADDON_IDS.dinero);
            const authUrl = dineroAddon?.meta?.authUrl;
            if (typeof authUrl === 'string' && authUrl.length > 0) {
                window.location.href = authUrl;
                return;
            }
            await fetchData();
            navigate('/settings/integration/dinero-setup');
        } catch (error) {
            console.error('error ', error);
            toast.error(t('Integration.FailedToActivate'));
        } finally {
            setLoadingState((prev) => ({ ...prev, dinero: false }));
        }
    };

    const handleEconomicActivate = async () => {
        const addon = getAddon(ADDON_IDS.economic);
        const authUrl = addon?.meta?.authUrl;
        if (typeof authUrl === 'string' && authUrl.length > 0) {
            window.location.replace(authUrl);
            return;
        }

        try {
            setLoadingState((prev) => ({ ...prev, economic: true }));
            await api.postApiAddonsUpsert({ addonName: 'economic' });
            const res = await api.getApiAddons();
            const posCategory = res.data?.find((c) => c.categoryId === 1);
            const economic = posCategory?.addons?.find((a) => a.id === ADDON_IDS.economic);
            const fetchedAuthUrl = economic?.meta?.authUrl;
            if (typeof fetchedAuthUrl === 'string' && fetchedAuthUrl.length > 0) {
                window.location.replace(fetchedAuthUrl);
                return;
            }
            await fetchData();
            navigate('/settings/integration/economic-setup');
        } catch (error) {
            console.error('error ', error);
            toast.error(t('Integration.FailedToActivate'));
        } finally {
            setLoadingState((prev) => ({ ...prev, economic: false }));
        }
    };

    const dineroAddon = getAddon(ADDON_IDS.dinero);
    const economicAddon = getAddon(ADDON_IDS.economic);
    const billyAddon = getAddon(ADDON_IDS.billy);

    return (
        <React.Fragment>
            <IntegrationCard
                title={t('POS.Integration')}
                description={t('POS.IntegrationDescription')}
                bottomDivider={false}
                topDivider={true}
                processContent={
                    isLoading ? (
                        <Stack spacing={2} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                            <Skeleton variant="rounded" width="30%" height={250} />
                            <Skeleton variant="rounded" width="30%" height={250} sx={{ mt: '0 !important' }} />
                        </Stack>
                    ) : (
                        <Grid2 container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 12, md: 12 }}>
                            <Grid2 size={{ xs: 12, sm: 6, xl: 4 }}>
                                <CardComponent
                                    label={t('Integration.Dinero')}
                                    intigrationName="dinero"
                                    image={dinero}
                                    isActive={Boolean(dineroAddon?.isActive)}
                                    isAvailableOnOutlet={dineroAddon?.isAvailableOnStore ?? false}
                                    onActivate={handleDineroActivate}
                                    setCloseingTab={setCloseTab}
                                    setIntegrationName={setIntegrationName}
                                    urlLink="/settings/integration/dinero-setup?status=success"
                                    activateLoading={Boolean(loadingState.dinero)}
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 12, sm: 6, xl: 4 }}>
                                <CardComponent
                                    label={t('Integration.Economic')}
                                    intigrationName="economic"
                                    image={economic_logo}
                                    isActive={Boolean(economicAddon?.isActive)}
                                    isAvailableOnOutlet={economicAddon?.isAvailableOnStore ?? false}
                                    onActivate={handleEconomicActivate}
                                    setCloseingTab={setCloseTab}
                                    setIntegrationName={setIntegrationName}
                                    urlLink="/settings/integration/economic-setup"
                                    activateLoading={Boolean(loadingState.economic)}
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 12, sm: 6, xl: 4 }}>
                                <CardComponent
                                    label={t('Integration.Billy')}
                                    intigrationName="billy"
                                    image={billy}
                                    isActive={Boolean(billyAddon?.isActive)}
                                    isAvailableOnOutlet={billyAddon?.isAvailableOnStore ?? false}
                                    onActivate={handleBillyActivate}
                                    setCloseingTab={setCloseTab}
                                    setIntegrationName={setIntegrationName}
                                    urlLink="/settings/integration/billy-setup"
                                    activateLoading={Boolean(loadingState.billy)}
                                    showDeactivate={false}
                                />
                            </Grid2>
                        </Grid2>
                    )
                }
            />
            {closeTab && (
                <POSDeleteModal
                    open={closeTab}
                    handleClose={() => setCloseTab(false)}
                    title={t('Integration.Deactivate')}
                    description={`${t('Integration.DeactivationMessage')} ${integrationName}?`}
                    onClickDismiss={() => setCloseTab(false)}
                    onClickConfirm={() => handleDeactive(integrationName)}
                    disabled={deactivateLoading}
                />
            )}
        </React.Fragment>
    );
};

export default POSIntegration;
